export interface WorkerDocument {
  workerId: string;
  type: 'carnet_sanidad' | 'certificado_unico_laboral' | 'otro';
  label: string | null;
  expiryDate: string | null;
}

export interface DocumentAlert {
  workerId: string;
  type: string;
  label: string;
  severity: 'vencido' | 'por_vencer';
  daysLeft: number;
  message: string;
}

const DEFAULT_LABEL: Record<WorkerDocument['type'], string> = {
  carnet_sanidad: 'Carnet de sanidad',
  certificado_unico_laboral: 'Certificado único laboral',
  otro: 'Documento',
};

const MS_PER_DAY = 86_400_000;

/** Días calendario entre dos fechas YYYY-MM-DD (sin zona horaria: aritmética en UTC). */
function daysBetween(fromISO: string, toISO: string): number {
  const [fy, fm, fd] = fromISO.split('-').map(Number);
  const [ty, tm, td] = toISO.split('-').map(Number);
  return (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / MS_PER_DAY;
}

const plural = (n: number): string => (n === 1 ? 'día' : 'días');

/**
 * Alerta si el documento está vencido (daysLeft < 0) o vence en <= warnDays.
 * Sin `expiryDate` no genera alerta. `today` en YYYY-MM-DD (America/Lima).
 * Resultado ordenado de lo más urgente a lo menos urgente.
 */
export function documentAlerts(
  docs: WorkerDocument[], today: string, warnDays = 30,
): DocumentAlert[] {
  const alerts: DocumentAlert[] = [];
  for (const d of docs) {
    if (!d.expiryDate) continue;
    const daysLeft = daysBetween(today, d.expiryDate);
    if (daysLeft > warnDays) continue;
    const label = d.label ?? DEFAULT_LABEL[d.type];
    const message =
      daysLeft < 0
        ? `${label} venció el ${d.expiryDate} (hace ${-daysLeft} ${plural(-daysLeft)})`
        : daysLeft === 0
          ? `${label} vence hoy (${d.expiryDate})`
          : `${label} vence el ${d.expiryDate} (en ${daysLeft} ${plural(daysLeft)})`;
    alerts.push({
      workerId: d.workerId,
      type: d.type,
      label,
      severity: daysLeft < 0 ? 'vencido' : 'por_vencer',
      daysLeft,
      message,
    });
  }
  return alerts.sort((a, b) => a.daysLeft - b.daysLeft);
}
